/**
 * threadHeaderTitleReveal — can you read the thread's name, and what does
 * tapping it do? (owner report, Miles 2026-09-18)
 *
 * HIS REPORT: "in mobile view, the title is too squished, and the user can't
 * even click on it to see the name of it. So ensure title — like thread
 * summary, I mean — is easy to see in a mobile view and dynamically from
 * mobile view to desktop view."
 *
 * Both halves were real, and the second explains the first. Since 2026-09-13
 * the phone header's title was the THREAD SWITCHER: tapping it opened the
 * thread list. So the one thing a person naturally taps when a name is cut off
 * — the name — was the one thing that could not show it, and the title sat
 * under a two-line `-webkit-line-clamp` with no other way to read the rest.
 *
 * WHAT MAKES THE CHANGE SAFE rather than a reversal: the ☰ that the same
 * 2026-09-13 round moved into the action rail ALREADY opens that list, first
 * in the rail and carrying the unread badge. Switching threads keeps its
 * control; the title gets its own job back.
 *
 * THE RULE IS MEASURED, NOT BREAKPOINTED, which is the "dynamically from
 * mobile view to desktop view" half. A clipped title is a fact about the
 * rendered box, so it is read off the box: a narrow DOCKED pane on a 27"
 * monitor clips exactly like a phone and had no reveal either, because the
 * clamp was container-keyed while the tap was viewport-keyed. One measurement
 * now answers for every width, and a rotation or a pane drag re-answers it
 * through the ResizeObserver rather than at a threshold.
 *
 * AND IT IS NEVER A CONTROL THAT FAILS AFTER THE CLICK (PR-1's rule): when the
 * whole name already fits there is nothing to reveal, so the label is not a
 * reveal button at all.
 *
 * Pure and DOM-shaped but React-free, so every rule here is a unit test.
 */

/**
 * Is this element's text actually cut off?
 *
 * Both axes, because the label wears two different truncations: one line with
 * an ellipsis at desktop widths (`white-space: nowrap` → scrollWidth overflows)
 * and a two-line `-webkit-line-clamp` at narrow ones (→ scrollHeight
 * overflows). Asking only about width would call every clamped phone title
 * readable, which is the case this exists for.
 *
 * The 1px tolerance is not superstition: sub-pixel layout makes scroll and
 * client sizes differ by fractions on a title that fits perfectly, and without
 * it a header would offer "show the full name" for a name already fully shown.
 */
export function isTitleClipped(el: {
  scrollWidth: number;
  clientWidth: number;
  scrollHeight: number;
  clientHeight: number;
} | null | undefined): boolean {
  if (!el) return false;
  const w = el.scrollWidth - el.clientWidth;
  const h = el.scrollHeight - el.clientHeight;
  return w > 1 || h > 1;
}

/** What the header label is, once measured. */
export type ThreadHeaderLabelMode = 'reveal' | 'collapse' | 'rename' | 'static';

/**
 * The one decision the component makes.
 *
 * `revealOnTap` is the surface's own fact — true where an inline rename is not
 * the tap's job (phones, where rename lives in the thread sheet since
 * 2026-09-13). Everywhere else the tap keeps renaming, unchanged, and a
 * clipped title is read through the tooltip instead.
 */
export function threadHeaderLabelMode(input: {
  revealOnTap: boolean;
  clipped: boolean;
  expanded: boolean;
  canRename: boolean;
}): ThreadHeaderLabelMode {
  if (input.revealOnTap) {
    if (input.expanded) return 'collapse';
    // Nothing is hidden ⇒ nothing to reveal ⇒ not a button.
    return input.clipped ? 'reveal' : 'static';
  }
  return input.canRename ? 'rename' : 'static';
}

/**
 * The accessible name and the tooltip.
 *
 * THE TOOLTIP CARRIES THE THREAD'S NAME, always, and that alone fixes the
 * desktop half: it used to read "click to rename this thread" — instructions
 * in the one place a truncated title could have been read on a pointer device.
 * The hint follows the name rather than replacing it.
 */
export function threadHeaderLabelText(
  title: string,
  mode: ThreadHeaderLabelMode,
): { ariaLabel: string; tooltip: string } {
  const name = (title ?? '').trim();
  switch (mode) {
    case 'reveal':
      return {
        ariaLabel: `current thread: ${name} — show the full name`,
        tooltip: `${name} — tap to see the full name`,
      };
    case 'collapse':
      return {
        ariaLabel: `current thread: ${name} — showing the full name`,
        tooltip: `${name} — tap to shorten`,
      };
    case 'rename':
      return {
        ariaLabel: `current thread: ${name} — click to rename`,
        tooltip: `${name} — click to rename this thread`,
      };
    default:
      return { ariaLabel: `current thread: ${name}`, tooltip: name };
  }
}
